import { Employee, Team, TemporaryLocation, Pet, PetSpecies } from '../types/employee';
import type { EmployeeRow, TeamRow, PetRow, PetInsert, PetUpdate } from '../lib/database.types';
import { supabase } from '../lib/supabase';
import { captureMutationError } from './errorReporting';

async function sha256(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function gravatarUrl(email: string): Promise<string> {
  const hash = await sha256(email.trim().toLowerCase());
  return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
}

export function rowToEmployee(row: EmployeeRow, fallbackAvatar: string): Employee {
  return {
    id: row.slug,
    name: row.name,
    role: row.role,
    department: row.department,
    employer: row.employer,
    email: row.email,
    phone: row.phone ?? undefined,
    bio: row.bio,
    startDate: row.start_date ?? '',
    skills: row.skills,
    languages: row.languages,
    avatar: row.avatar_url ?? fallbackAvatar,
    avatarHover: row.avatar_hover_url ?? row.avatar_url ?? fallbackAvatar,
    location: {
      city: row.city,
      country: row.country,
      coordinates: {
        latitude: row.latitude ?? 0,
        longitude: row.longitude ?? 0,
      },
      timezone: row.timezone,
      coordinatesManual: row.coordinates_manual,
    },
    githubHandle: row.github_handle ?? undefined,
    newHireDeckUrl: row.new_hire_deck_url ?? undefined,
    socialMedia: row.social_media as Employee['socialMedia'],
    meetColleagueAvailable: row.meet_colleague_available,
    meetColleagueUnavailableUntil: row.meet_colleague_unavailable_until ?? undefined,
  };
}

export function rowToTemporaryLocation(row: {
  id: string;
  employee_id: string;
  city: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
  label: string;
  start_date: string;
  end_date: string;
}): TemporaryLocation {
  return {
    id: row.id,
    employeeId: row.employee_id,
    city: row.city,
    country: row.country,
    coordinates: {
      latitude: row.latitude ?? 0,
      longitude: row.longitude ?? 0,
    },
    timezone: row.timezone,
    label: row.label,
    startDate: row.start_date,
    endDate: row.end_date,
  };
}

export function rowToTeam(row: TeamRow): Team {
  return {
    id: row.id,
    name: row.name,
    iconType: row.icon_type,
    iconValue: row.icon_value,
  };
}

export const fetchAllTeams = async (): Promise<Team[]> => {
  const { data, error } = await supabase.from('teams').select('*').order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToTeam);
};

export const addEmployeeTeam = async (employeeId: string, teamId: string): Promise<void> => {
  const { error } = await supabase.from('employee_teams').insert({ employee_id: employeeId, team_id: teamId });
  if (error) {
    captureMutationError(error, { operation: 'addEmployeeTeam', table: 'employee_teams', employeeId, entityId: teamId });
    throw new Error(error.message);
  }
};

export const removeEmployeeTeam = async (employeeId: string, teamId: string): Promise<void> => {
  const { error } = await supabase.from('employee_teams').delete().eq('employee_id', employeeId).eq('team_id', teamId);
  if (error) {
    captureMutationError(error, { operation: 'removeEmployeeTeam', table: 'employee_teams', employeeId, entityId: teamId });
    throw new Error(error.message);
  }
};

export const createTeam = async (name: string, iconType: Team['iconType'], iconValue: string): Promise<Team> => {
  const { data, error } = await supabase
    .from('teams')
    .insert({ name: name.trim(), icon_type: iconType, icon_value: iconValue })
    .select()
    .single();
  if (error) {
    captureMutationError(error, { operation: 'createTeam', table: 'teams' });
    throw new Error(error.message);
  }
  return rowToTeam(data);
};

export const updateTeam = async (id: string, updates: { name?: string; iconType?: Team['iconType']; iconValue?: string }): Promise<Team> => {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name.trim();
  if (updates.iconType !== undefined) payload.icon_type = updates.iconType;
  if (updates.iconValue !== undefined) payload.icon_value = updates.iconValue;
  const { data, error } = await supabase.from('teams').update(payload).eq('id', id).select().single();
  if (error) {
    captureMutationError(error, { operation: 'updateTeam', table: 'teams', entityId: id });
    throw new Error(error.message);
  }
  return rowToTeam(data);
};

export const deleteTeam = async (id: string): Promise<void> => {
  const { error } = await supabase.from('teams').delete().eq('id', id);
  if (error) {
    captureMutationError(error, { operation: 'deleteTeam', table: 'teams', entityId: id });
    throw new Error(error.message);
  }
};

export function rowToPet(row: PetRow): Pet {
  return {
    id: row.id,
    employeeId: row.employee_id,
    name: row.name,
    species: row.species,
    role: row.role ?? undefined,
    bio: row.bio,
    photoUrl: row.photo_url ?? undefined,
  };
}

export interface PetWithOwner extends Pet {
  ownerName: string;
  ownerSlug: string;
  ownerAvatar?: string;
}

export const fetchAllPets = async (): Promise<PetWithOwner[]> => {
  const { data, error } = await supabase
    .from('pets')
    .select('*, employees(name, slug, avatar_url)')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).map((row: PetRow & { employees: { name: string; slug: string; avatar_url: string | null } | null }) => ({
    ...rowToPet(row),
    species: row.species as PetSpecies,
    ownerName: row.employees?.name ?? '',
    ownerSlug: row.employees?.slug ?? '',
    ownerAvatar: row.employees?.avatar_url ?? undefined,
  }));
};

export const fetchPetsForEmployee = async (employeeId: string): Promise<Pet[]> => {
  const { data, error } = await supabase.from('pets').select('*').eq('employee_id', employeeId).order('created_at');
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToPet);
};

export const createPet = async (pet: PetInsert): Promise<Pet> => {
  const { data, error } = await supabase.from('pets').insert(pet).select().single();
  if (error) {
    captureMutationError(error, { operation: 'createPet', table: 'pets', employeeId: pet.employee_id });
    throw new Error(error.message);
  }
  return rowToPet(data);
};

export const updatePet = async (id: string, updates: PetUpdate): Promise<Pet> => {
  const { data, error } = await supabase.from('pets').update(updates).eq('id', id).select().single();
  if (error) {
    captureMutationError(error, { operation: 'updatePet', table: 'pets', entityId: id });
    throw new Error(error.message);
  }
  return rowToPet(data);
};

export const deletePet = async (id: string): Promise<void> => {
  const { error } = await supabase.from('pets').delete().eq('id', id);
  if (error) {
    captureMutationError(error, { operation: 'deletePet', table: 'pets', entityId: id });
    throw new Error(error.message);
  }
};

export const fetchEmployees = async (): Promise<Employee[]> => {
  const [employeesResult, tempLocsResult, employeeTeamsResult, petsResult] = await Promise.all([
    supabase.from('employees').select('*').order('name'),
    supabase.from('temporary_locations').select('*'),
    supabase.from('employee_teams').select('employee_id, teams(*)'),
    supabase.from('pets').select('*'),
  ]);

  if (employeesResult.error) throw new Error(employeesResult.error.message);

  const tempLocsByEmployeeId = new Map<string, TemporaryLocation[]>();
  for (const row of tempLocsResult.data ?? []) {
    const loc = rowToTemporaryLocation(row);
    const arr = tempLocsByEmployeeId.get(row.employee_id) ?? [];
    arr.push(loc);
    tempLocsByEmployeeId.set(row.employee_id, arr);
  }

  const teamsByEmployeeId = new Map<string, Team[]>();
  for (const row of (employeeTeamsResult.data ?? []) as { employee_id: string; teams: TeamRow }[]) {
    if (!row.teams) continue;
    const arr = teamsByEmployeeId.get(row.employee_id) ?? [];
    arr.push(rowToTeam(row.teams));
    teamsByEmployeeId.set(row.employee_id, arr);
  }

  const petsByEmployeeId = new Map<string, Pet[]>();
  for (const row of (petsResult.data ?? []) as PetRow[]) {
    const arr = petsByEmployeeId.get(row.employee_id) ?? [];
    arr.push(rowToPet(row));
    petsByEmployeeId.set(row.employee_id, arr);
  }

  const employees: Employee[] = await Promise.all(
    (employeesResult.data ?? []).map(async row => {
      const fallback = await gravatarUrl(row.email);
      const emp = rowToEmployee(row, fallback);
      emp.temporaryLocations = tempLocsByEmployeeId.get(row.id) ?? [];
      emp.teams = teamsByEmployeeId.get(row.id) ?? [];
      emp.pets = petsByEmployeeId.get(row.id) ?? [];
      return emp;
    })
  );

  return employees;
};
